import { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import * as Sentry from '@sentry/react';
import { tryCatch } from '@/try-catch';
import Axios from 'axios';
import {
  auth0Exports,
  devUserId,
  isReauthenticationError,
  redirectToSessionEnded,
} from '@/features/auth/utils';
import { t } from '@/features/i18n/utils';

const SENTRY_IGNORED_STATUSES = [401, 403];

export const isWorkingCopyPreparing = (error: unknown) =>
  Axios.isAxiosError(error) &&
  error.response?.status === 409 &&
  error.response?.data?.detail?.code === 'working_copy_preparing';

const authRequestInterceptor = async (request: InternalAxiosRequestConfig) => {
  if (devUserId) {
    request.params = { ...request.params, userId: devUserId };
    return request;
  }

  await auth0Exports.waitUntilReady();

  if (auth0Exports.getIsAuthenticated()) {
    const getAccessTokenSilently = auth0Exports.getAccessTokenSilently();
    if (getAccessTokenSilently) {
      const { data: token, error } = await tryCatch(getAccessTokenSilently);
      if (error) {
        Sentry.captureException(error, {
          extra: {
            description: 'Silent token refresh failed before API request',
            url: api.getUri(request),
            method: request.method,
          },
        });
        if (isReauthenticationError(error)) redirectToSessionEnded('sessionExpired');
        throw error;
      }
      if (token && request.headers) request.headers.Authorization = `Bearer ${token}`;
    }
  }

  return request;
};

const SESSION_ENDED_REASONS = new Map<string, 'accessRevoked' | 'invitationExpired'>([
  ['User is not enabled in organization', 'accessRevoked'],
  ['Organization is not enabled', 'accessRevoked'],
  ['Invitation expired', 'invitationExpired'],
]);

const sessionEndedInterceptor = (error: AxiosError) => {
  const detail = (error.response?.data as { detail?: string } | undefined)?.detail;
  const reason = error.response?.status === 403 && detail && SESSION_ENDED_REASONS.get(detail);
  if (reason) redirectToSessionEnded(reason);
  return Promise.reject(error);
};

const sentryErrorInterceptor = (error: AxiosError) => {
  if (isWorkingCopyPreparing(error)) return Promise.reject(error);
  if (error.response?.status && !SENTRY_IGNORED_STATUSES.includes(error.response?.status)) {
    Sentry.captureException(error, {
      tags: {
        errorType: 'api_error',
        statusCode: error.response?.status,
      },
      extra: {
        url: error.config && api.getUri(error.config),
        method: error.config?.method,
        responseData: error.response?.data,
      },
    });
  }

  return Promise.reject(error);
};

type ApiErrorCode = {
  code: string;
  reasons: string[];
};

export const unpackApiErr = (err: Error | null) => {
  if (err) {
    if ('response' in err) {
      const error = err.response as AxiosResponse;
      if (Array.isArray(error.data) && error.data.length > 0) {
        const apiErrors = error.data as ApiErrorCode[];
        const reasons = apiErrors.flatMap((e) => e.reasons);
        return reasons[0] ?? '';
      }
      if (typeof error.data?.detail === 'string') {
        if (/^Missing required claim:/i.test(error.data.detail))
          return t({
            en: "You don't have access to do this in this project. Ask a project owner for access.",
            nb: 'Du har ikke tilgang til å gjøre dette i prosjektet. Be en prosjekteier om tilgang.',
          });
        if (/^Missing required org claim:/i.test(error.data.detail))
          return t({
            en: "You don't have permission to do this. Ask an admin for access.",
            nb: 'Du har ikke tillatelse til å gjøre dette. Be en administrator om tilgang.',
          });
        return error.data.detail;
      }
      if (typeof error.data?.detail?.message === 'string') return error.data.detail.message;
    }
  }
  return '';
};

export const withBypassUserId = <
  T extends Record<string, string | number | boolean | Array<string | number> | undefined>,
>(
  params: T,
) => (devUserId ? { ...params, userId: devUserId } : params);

export const api = Axios.create({
  baseURL: `${import.meta.env.VITE_AI_API_URL}`,
  paramsSerializer: { indexes: null },
});
api.interceptors.request.use(authRequestInterceptor);
api.interceptors.response.use((response) => response, sessionEndedInterceptor);
api.interceptors.response.use((response) => response, sentryErrorInterceptor);

const getToken = async () => {
  if (!auth0Exports.getIsAuthenticated()) return '';
  const getAccessTokenSilently = auth0Exports.getAccessTokenSilently();
  if (!getAccessTokenSilently) return '';
  const { data, error } = await tryCatch(getAccessTokenSilently);
  if (isReauthenticationError(error)) redirectToSessionEnded('sessionExpired');
  return data ?? '';
};

export const streamRequestHeaders = async () => {
  const headers: Record<string, string> = { Accept: 'text/event-stream' };
  const token = await getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
};

export type QueryWithEnabled<T> = T & { enabled?: boolean };

export type PaginationResponse<T> = {
  data: T[];
  pageCount: number;
  pageSize: number;
  totalCount: number;
};
